import type { KnowledgeBaseApi } from '@liks/backend-svc-api/esm/apis/KnowledgeBaseApi';
import type {
  KnowledgeBaseGroupAssignment,
  KnowledgeBaseMembership,
} from '@liks/backend-svc-api/esm/models/index';
import type { KnowledgeMemberRole, WithResourceId } from '../types/knowledge';
import { hasId } from './knowledgeServiceGuards';

export async function listMembers(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
): Promise<
  Array<WithResourceId<KnowledgeBaseMembership> & { userId: string }>
> {
  const members =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseMembershipResourceListKnowledgeBaseMembers(
      { knowledgeBaseId: databaseId },
    );
  return members.filter(
    (
      member,
    ): member is WithResourceId<KnowledgeBaseMembership> & {
      userId: string;
    } =>
      hasId(member) &&
      typeof member.userId === 'string' &&
      member.userId.length > 0,
  );
}

export async function inviteMember(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  userId: string,
  role: KnowledgeMemberRole,
): Promise<void> {
  await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseMembershipResourceInviteKnowledgeBaseMember(
    {
      knowledgeBaseId: databaseId,
      knowledgeBaseMembership: { userId, role },
    },
  );
}

export async function updateMemberRole(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  member: KnowledgeBaseMembership,
  role: KnowledgeMemberRole,
): Promise<void> {
  if (
    typeof member.id !== 'string' ||
    member.id.length === 0 ||
    member.userId == null
  ) {
    throw new Error('Cannot update membership without id and userId');
  }

  await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseMembershipResourceUpdateKnowledgeBaseMembership(
    {
      knowledgeBaseId: databaseId,
      id: member.id,
      knowledgeBaseMembership: {
        id: member.id,
        userId: member.userId,
        role,
      },
    },
  );
}

export async function removeMember(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  memberId: string,
): Promise<void> {
  await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseMembershipResourceRemoveKnowledgeBaseMember(
    { knowledgeBaseId: databaseId, id: memberId },
  );
}

export async function listGroupAssignments(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
): Promise<Array<WithResourceId<KnowledgeBaseGroupAssignment>>> {
  const assignments =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseGroupAssignmentResourceListKnowledgeBaseGroups(
      { knowledgeBaseId: databaseId },
    );
  return assignments.filter(hasId);
}

export async function assignGroup(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  userGroupId: string,
  role: KnowledgeMemberRole,
): Promise<KnowledgeBaseGroupAssignment> {
  return databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseGroupAssignmentResourceAssignKnowledgeBaseGroup(
    {
      knowledgeBaseId: databaseId,
      knowledgeBaseGroupAssignment: { userGroupId, role },
    },
  );
}

export async function updateGroupAssignmentRole(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  assignment: KnowledgeBaseGroupAssignment,
  role: KnowledgeMemberRole,
): Promise<KnowledgeBaseGroupAssignment> {
  if (
    typeof assignment.id !== 'string' ||
    assignment.id.length === 0 ||
    assignment.userGroupId == null
  ) {
    throw new Error(
      'Cannot update knowledge base group assignment without id and userGroupId',
    );
  }

  return databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseGroupAssignmentResourceUpdateKnowledgeBaseGroupAssignment(
    {
      knowledgeBaseId: databaseId,
      id: assignment.id,
      knowledgeBaseGroupAssignment: {
        id: assignment.id,
        userGroupId: assignment.userGroupId,
        role,
      },
    },
  );
}

export async function unassignGroup(
  databaseApi: KnowledgeBaseApi,
  databaseId: string,
  assignmentId: string,
): Promise<void> {
  await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseGroupAssignmentResourceUnassignKnowledgeBaseGroup(
    { knowledgeBaseId: databaseId, id: assignmentId },
  );
}
